import { Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';

export const CartDrawer = () => {
  const { 
    cartItems, 
    cartTotal, 
    isCartOpen, 
    setIsCartOpen, 
    updateQuantity, 
    removeFromCart 
  } = useCart();

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent 
        side="right" 
        className="w-full sm:w-96 bg-[#FAF9F6] flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl text-[#2A1A14] flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-[#7B3E19]" />
            Your Cart
          </SheetTitle>
          <SheetDescription className="sr-only">Shopping cart contents</SheetDescription>
        </SheetHeader>

        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
            <ShoppingBag className="w-16 h-16 text-[#E3DBD1] mb-4" />
            <p className="text-[#786C65] mb-4">Your cart is empty</p>
            <Button
              onClick={() => setIsCartOpen(false)}
              className="bg-[#7B3E19] hover:bg-[#633113] text-white rounded-sm"
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto py-4">
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div 
                    key={item.product_id} 
                    className="flex gap-4 p-3 bg-white rounded-sm border border-[#E3DBD1]"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-sm"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-[#2A1A14] text-sm truncate">
                        {item.name.split(' - ')[0]}
                      </h4>
                      <p className="text-[#7B3E19] font-semibold mt-1">
                        ${item.price.toFixed(2)}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="p-1 hover:bg-[#EAE3D9] rounded"
                          data-testid={`cart-decrease-${item.product_id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-6 text-center text-sm" data-testid={`cart-quantity-${item.product_id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="p-1 hover:bg-[#EAE3D9] rounded"
                          data-testid={`cart-increase-${item.product_id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.product_id)}
                      className="p-1 hover:bg-red-50 rounded self-start"
                      data-testid={`cart-remove-${item.product_id}`}
                    >
                      <X className="w-5 h-5 text-[#786C65] hover:text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Cart Footer */}
            <div className="border-t border-[#E3DBD1] pt-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-[#786C65]">Subtotal</span>
                <span className="text-xl font-heading font-semibold text-[#2A1A14]" data-testid="cart-total">
                  ${cartTotal.toFixed(2)}
                </span>
              </div>
              <p className="text-sm text-[#786C65]">
                Shipping calculated at checkout
              </p>
              <Link to="/checkout" onClick={() => setIsCartOpen(false)}>
                <Button 
                  className="w-full bg-[#7B3E19] hover:bg-[#633113] text-white rounded-sm py-6 text-lg"
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                </Button>
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
